/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { type ValidationErrors, Validators } from '@angular/forms';
import { type ColumnDef, type Command,
type JobResult, type Rest, type TableConfig, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract, Status, UtilService } from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { ToolService } from 'app/common/services';
import { type WindowsLocalGroup, type WindowsLocalUser, type WindowsUserSecurityPolicy } from 'app/common/types/mo';
import { SWITCH } from 'app/config/switch';
import { finalize } from 'rxjs/operators';

import { WindowsLocalAccessUserService } from '../windows-local-access-user.service';
import { AccountTypeE, PasswordComplexE, WindowsLocalAccessUserStrService } from '../windows-local-access-user-str.service';

@Component({
    selector: 'app-create-windows-local-access-user',
    templateUrl: './create-windows-local-access-user.component.html',
    styleUrls: ['./create-windows-local-access-user.component.scss'],
})
export class CreateWindowsLocalAccessUserComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() vstore: SelectOption;

    i18n = this.str.i18n;
    SWITCH = SWITCH;

    vm: FormGroup;
    errorMessage: any;
    get vmValue(): WindowsLocalUser {
        return this.vm ? this.vm.value : null;
    }
    nameTips: string[] = [];
    passwordTips: string[] = [];

    vstoreRest: Rest;
    groupRest: Rest;
    groupTableConfig: TableConfig;
    groupColumnDefs: ColumnDef[];
    selectedGroups: WindowsLocalGroup[] = [];

    constructor(
        injector: Injector,
        public str: WindowsLocalAccessUserStrService,
        private service: WindowsLocalAccessUserService,
        private toolService: ToolService,
        private utilService: UtilService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initGroupTable();
        this.getSecurityPolicy(this.vmValue.vstoreId);

        this.vstoreRest = this.service.getVstoreRest();
        this.pageReady = true;
        this.windowVisible = true;
    }

    initVm(): void {
        this.vm = new FormGroup({
            vstoreId: new FormControl(this.vstore && this.vstore.value ? this.vstore.value : null, {
                validators: [
                    Validators.required,
                ],
            }),
            NAME: new FormControl(''),
            PASSWORD: new FormControl(''),
            CONFIRMPASSWORD: new FormControl('', {
                validators: [
                    Validators.required,
                ],
            }),
            STATUSENABLE: new FormControl(true),
            DESCRIPTION: new FormControl('', {
                validators: [
                    DMValidators.rangeByteLength(0, 256),
                ],
            }),
        }, {
            updateOn: 'blur',
            validators: [
                DMValidators.equal(['PASSWORD', 'CONFIRMPASSWORD']),
            ],
        });

        this.errorMessage = {
            vstoreId: {
                required: this.toolService.requiredErr(this.i18n.vstoreLab),
            },
            NAME: {
                required: this.toolService.requiredErr(this.i18n.nameLab),
                nameFormat: this.i18n.userNameFormatErr,
            },
            PASSWORD: {
                required: this.toolService.requiredErr(this.i18n.passwordLab),
                equalReverse: this.i18n.passwordReversTip,
            },
            CONFIRMPASSWORD: {
                required: this.toolService.requiredErr(this.i18n.confirmPasswordLab),
                equal: this.i18n.confirmPasswordErr,
            },
            DESCRIPTION: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 256),
            },
        };
    }

    /**
     * 初始化用户组表格
     */
    initGroupTable(): void {
        this.groupRest = this.service.getGroupRest(this.vstore.value);
        this.groupTableConfig = {
            dataKey: 'RID',
        };
        this.groupColumnDefs = [
            {
                field: 'NAME',
                header: this.i18n.nameLab,
                filterType: 'search',
                className: ['table-cell-text-pre'],
            },
            {
                field: 'RID',
                header: this.i18n.ridLab,
                filterType: 'search',
                sortable: true,
                width: 100,
            },
            {
                field: 'GROUPTYPE',
                columnFilter: true,
                header: this.i18n.groupType,
                filterType: 'dropdown',
                options: this.str.groupTypeOpt,
                render: (value) => this.utilService.getLabelFromOptions(this.str.groupTypeOpt, value),
            },
        ];
    }

    /**
     * 名称变化，重新触发密码校验
     */
    onNameChange(): void {
        this.vm.get('PASSWORD').updateValueAndValidity();
    }

    /**
     * 名称格式校验
     */
    nameFormatValidator = (control: FormControl): ValidationErrors => {
        // eslint-disable-next-line no-control-regex
        const rule = /[\"\/\\\]\[\:\;\|\=\,\+\*\?\<\>\@\s\x00-\x1F\x7F-\x9F]+/;
        const value: string = control.value;

        // 1、不能为"/\][:;|=,+*?<>@、空格以及控制字符，可以为中文等字符
        if (rule.test(value)) {
            return { nameFormat: true };
        }

        // 2、不能以.结尾
        if (value.endsWith('.')) {
            return { nameFormat: true };
        }
        return null;
    };

    /**
     * 查询安全策略
     */
    getSecurityPolicy(vstoreId: string): void {
        let data: WindowsUserSecurityPolicy;

        if (!vstoreId) {
            this.updateVmValidators(data);
            return;
        }

        this.service.getSecurityPolicy(vstoreId, { mask: true })
            .pipe(
                finalize(() => {
                    this.updateVmValidators(data);
                }),
            )
            .subscribe((res) => {
                data = res.data;
            });
    }

    /**
     * 更新表单校验器
     */
    updateVmValidators(data?: WindowsUserSecurityPolicy): void {
        const vm = this.vm;
        const nameMinLength = data ? Number(data.USERMINLENGTH) : 1;
        const passwordMinLength = data ? Number(data.PASSWORDMINLENGTH) : 6;
        const passwordMaxLength = data ? Number(data.PASSWORDMAXLENGTH) : 32;
        const passwordComplex = data ? Number(data.PWDCOMPLEX) : 0;
        const passwordCharRepeat = data ? Number(data.REDUPLICATECHARCOUNT) : 3;

        vm.get('NAME').setValidators([
            Validators.required,
            this.nameFormatValidator,
            DMValidators.rangeByteLength(nameMinLength, 20),
        ]);

        vm.get('PASSWORD').setValidators([
            Validators.required,
            DMValidators.rangeByteLength(passwordMinLength, passwordMaxLength),
            DMValidators.complex(passwordComplex === PasswordComplexE.Normal ? 'ramdom' : 'middle', 2),
            ...(passwordCharRepeat ? [DMValidators.charRepeat(passwordCharRepeat)] : []),
            DMValidators.equalReverse('NAME'),
        ]);

        this.errorMessage.NAME.rangeByteLength = this.toolService.rangeByteLengthErr(nameMinLength, 20);
        this.errorMessage.PASSWORD.rangeByteLength = this.toolService.rangeByteLengthErr(passwordMinLength, passwordMaxLength);
        this.errorMessage.PASSWORD.complex = passwordComplex === PasswordComplexE.Normal
            ? this.i18n.passwordComplexNormalLab
            : this.i18n.passwordComplexHighLab;
        this.errorMessage.PASSWORD.charRepeat = this.str.chartRepeatErr(passwordCharRepeat);

        this.nameTips = [this.errorMessage.NAME.rangeByteLength, this.errorMessage.NAME.nameFormat];
        this.passwordTips = [
            this.errorMessage.PASSWORD.rangeByteLength,
            this.errorMessage.PASSWORD.complex,
            ...(passwordCharRepeat ? [this.errorMessage.PASSWORD.charRepeat] : []),
            this.errorMessage.PASSWORD.equalReverse,
        ];
    }

    /**
     * 切换租户后更新表单校验器
     */
    onVstoreChange({ value }): void {
        this.getSecurityPolicy(value);
    }

    /**
     * 构建下发命令
     */
    buildCmds(): Command[] {
        const cmds: Command[] = [
            this.buildCreateCmd(),
            ...this.buildAddToGroupCmds(),
        ];

        return cmds;
    }

    /**
     * 构建创建本地认证用户的命令
     */
    buildCreateCmd(): Command {
        const vmValue: WindowsLocalUser = { ...this.vmValue };

        return {
            id: 'create-user',
            name: this.str.createResult(vmValue.NAME),
            action: this.service.create(vmValue),
        };
    }

    /**
     * 构建添加到用户组命令
     */
    buildAddToGroupCmds(): Command[] {
        const vmValue = this.vmValue;
        return this.selectedGroups.map((group) => ({
            deps: ['create-user'],
            name: this.str.addToGroupResult(vmValue.NAME, group.NAME),
            action: () => this.service.addToGroup({
                vstoreId: vmValue.vstoreId,
                NAME: vmValue.NAME,
                GROUPNAME: group.NAME,
                ACCOUNTTYPE: AccountTypeE.User,
            }),
        }));
    }

    onComplete = (results: JobResult[]): void => {
        const userData: WindowsLocalUser[] = [];
        results.forEach((item) => {
            if (item.status === Status.success && item.response.data && item.response.data.NAME) {
                userData.push(item.response.data);
            }
        });
        this.closeArgs.data = userData;
        this.closeArgs.refresh = results.some((a) => a.status === Status.success);
    };
}
